import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';

interface AnnotatedImagesBlockProps {
  /** Signed URLs of the annotated (marked-up) versions of the user's photos. */
  images: string[];
  /** Opens the zoom lightbox for the image at this index. */
  onImageClick?: (index: number) => void;
  /** "What the photo showed" — opened from the info icon on the photo. Rendered
   *  (and therefore fetched) only once opened. */
  details?: React.ReactNode;
}

/**
 * The vision agent's markings, shown explicitly in the chat thread.
 *
 * The annotated image is the user's own photo with a callout drawn around every
 * part that was identified — it is the surface they confirm or correct from, so
 * it belongs in the conversation at full size, not only as a thumbnail in the
 * floating tracker panel. Each image is clickable to zoom.
 */
const AnnotatedImagesBlock: React.FC<AnnotatedImagesBlockProps> = ({ images, onImageClick, details }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);
  if (!images || images.length === 0) return null;
  const detailsLabel = t('chat.botMessage.annotatedImageDetails', { defaultValue: 'What the photo showed' }) as string;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-2 sm:p-3 shadow-sm">
      <div className="mb-1 flex items-center gap-1.5">
        <svg
          className="h-3.5 w-3.5 flex-shrink-0 text-primary-500"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <rect x="2.5" y="4" width="15" height="12" rx="2" />
          <circle cx="12.5" cy="9.5" r="3" />
          <path d="M2.5 14l4-4 3 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {t('chat.botMessage.annotatedImageTitle', { defaultValue: 'What I marked on your photo' })}
        </span>
      </div>

      <p className="mb-2 text-[12px] leading-snug text-gray-500">
        {t('chat.botMessage.annotatedImageHint', {
          defaultValue: 'Every marking is a part I identified. Tap to enlarge — tell me if I marked the wrong one.',
        })}
      </p>

      <div className="relative">
      <div className={`grid gap-2 ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {images.map((imageUrl, index) => (
          <button
            key={`${imageUrl}-${index}`}
            type="button"
            onClick={() => onImageClick?.(index)}
            className="group relative block w-full overflow-hidden rounded-lg border border-gray-200 bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            aria-label={t('chat.botMessage.annotatedImageZoom', {
              defaultValue: 'Enlarge marked photo {{n}}',
              n: index + 1,
            }) as string}
          >
            <img
              src={imageUrl}
              alt={t('chat.botMessage.annotatedImageAlt', {
                defaultValue: 'Your photo with the identified parts marked',
              }) as string}
              className="max-h-[520px] w-full object-contain transition-opacity group-hover:opacity-90"
              loading="lazy"
            />
          </button>
        ))}
      </div>
      {details && (
        <button
          type="button"
          data-testid="annotated-details-toggle"
          onClick={() => setShowDetails(v => !v)}
          aria-expanded={showDetails}
          aria-label={detailsLabel}
          title={detailsLabel}
          className={`absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full shadow-md ring-1 ring-black/10 focus:outline-none focus:ring-2 focus:ring-primary-400 ${
            showDetails ? 'bg-primary-600 text-white' : 'bg-white/90 text-gray-700 hover:bg-white'}`}
        >
          {showDetails ? <X className="h-4 w-4" /> : <Info className="h-4 w-4" />}
        </button>
      )}
      </div>

      {details && showDetails && (
        <div data-testid="annotated-details" className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-left">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{detailsLabel}</div>
          {details}
        </div>
      )}
    </div>
  );
};

export default AnnotatedImagesBlock;
